'use client';

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, Loader2, Crosshair, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAddressStore } from '@/store/address-store';
import { useMarketplaceStore } from '@/store/marketplace-store';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Place {
  name: string;
  formatted_address: string;
  place_id: string;
  geometry?: {
    location: {
      lat: number;
      lng: number;
    }
  }
}

export const MobileAddressBar: React.FC = () => {
  const router = useRouter();
  const { address, setAddress } = useAddressStore();
  const { config } = useMarketplaceStore();
  const [places, setPlaces] = useState<Place[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Initialize input with address
  useEffect(() => {
    if (address && !searchQuery) {
      setSearchQuery(address);
    }
  }, [address]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search places with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 && searchQuery !== address) {
        handleSearchPlaces();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click handler for search input
  const handleInputClick = () => {
    router.push('/search');
  };

  const handleSearchPlaces = async () => {
    if (searchQuery.length < 3) return;
    
    setIsSearching(true);
    try {
      const googleKey = config?.settings?.webapp_google_api_key || config?.google_maps_api_key || "";
      const response = await fetch(
        `/api/places/search?query=${encodeURIComponent(searchQuery)}&key=${googleKey}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch places');
      }
      
      const data = await response.json();
      setPlaces(data.results || []);
      setShowDropdown(data.results && data.results.length > 0);
    } catch (error) {
      console.error('Error searching places:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlaceSelect = (place: Place) => {
    setSearchQuery(place.formatted_address);
    setAddress(place.formatted_address);
    setShowDropdown(false);
    
    // Store location data in store if geometry is available
    if (place.geometry?.location) {
      const { lat, lng } = place.geometry.location;
      // If the marketplace store has a setLocation method, use it
      if (typeof useMarketplaceStore().setLocation === 'function') {
        useMarketplaceStore().setLocation(lat, lng, place.formatted_address);
      }
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation not supported',
        description: 'Your browser does not support geolocation',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const googleKey = config?.settings?.webapp_google_api_key || config?.google_maps_api_key || "";
          
          // Get address from coordinates using our API
          const response = await fetch(`/api/places/search?lat=${latitude}&lng=${longitude}`);
          
          if (!response.ok) throw new Error('Failed to get address from coordinates');
          
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            const formattedAddress = data.results[0].formatted_address;
            setSearchQuery(formattedAddress);
            setAddress(formattedAddress);
            
            toast({
              title: 'Location updated',
              description: 'Your delivery location has been updated',
              variant: 'default'
            });
            
            // If the marketplace store has a setLocation method, use it
            if (typeof useMarketplaceStore().setLocation === 'function') {
              useMarketplaceStore().setLocation(latitude, longitude, formattedAddress);
            }
          }
        } catch (error) {
          console.error('Error getting current location:', error);
          toast({
            title: 'Error getting location',
            description: 'Please try again or enter address manually',
            variant: 'destructive'
          });
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        
        let errorMessage = 'Failed to get your location';
        if (error.code === 1) {
          errorMessage = 'Location access denied. Please allow location access.';
        } else if (error.code === 2) {
          errorMessage = 'Location unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }
        
        toast({
          title: 'Location Error',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    );
  };

  return (
    <div className="w-full">
      {/* Address bar header */}
      <div className="flex items-center justify-between mb-1">
        <button 
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
          className="flex items-center"
        >
          <MapPin className="h-5 w-5 mr-2" />
          <span className="font-medium">Address</span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </button>
        
        <Button
          size="sm"
          variant="ghost"
          onClick={getCurrentLocation}
          className="p-1 text-white hover:bg-[#900f21]/80"
          disabled={isLocating}
        >
          {isLocating ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Crosshair className="h-5 w-5" />
          )}
        </Button>
      </div>
      
      {/* Current address display */}
      <div className="mb-2 text-white text-sm">
        <div className="truncate pr-8">
          {address || 'Select your delivery location'}
        </div>
      </div>
      
      {/* Search input that shows suggestions */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Enter your delivery location"
          className="w-full pl-4 pr-10 py-5 h-12 text-gray-800 bg-white rounded-md border-none"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
          </div>
        )}
        {!isSearching && (
          <div 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
            onClick={handleInputClick}
          >
            <Search className="h-5 w-5 text-[#900f21]" />
          </div>
        )}
      </div>
      
      {/* Dropdown for search results */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute left-4 right-4 mt-1 bg-white rounded-md shadow-lg z-50 overflow-hidden max-h-60 overflow-y-auto"
        >
          {places?.map((place) => (
            <div
              key={place.place_id}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
              onClick={() => handlePlaceSelect(place)}
            >
              <div className="font-medium">{place.name}</div>
              <div className="text-sm text-gray-600">{place.formatted_address}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileAddressBar;
